import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBeforeUnload } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Autocomplete,
  CircularProgress,
  Dialog,
  IconButton,
  MenuItem,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { RegistryWidgetsType, RJSFValidationError } from '@rjsf/utils';
import { TextSpan, TextBlock } from '../../Atoms/Text';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { Account, Attachment, InventoryAsset, Product, OrderRequestSchema } from '../../../types';
import { ServiceRequest } from '../../../Api/ServiceRequest/serviceRequestApi';
import { getProductSubject, useProductsStore } from '../../../store/Product';
import { getInventorySubject, useInventory } from '../../../store/Inventory';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { addServiceRequest } from '../../../store/ServiceRequest';
import { MultiNewLocationInput } from '../MultiNewLocationInput';
import {
  getAccounts,
  getAccountScope,
  getPreferencesByKey,
  getSelectedAccountsSubject,
  getUser,
  getUserPreferencesSubject,
  isAdvancedOrderFormEnabled,
  OrderRequestPreferences
} from '../../../store/User';
import { userInSkipRoles } from '../../../store/Role';
import {
  isPendingUpload,
  isUnsentFile,
  refusedFile,
  stageAttachments,
  uploadStagedAttachments
} from '../../../store/StagedAttachment';
import { AttachmentQueue } from '../AttachmentQueue';
import { AttachmentTableCTA } from '../AttachmentCTA/AttachmentTableCTA';
import { NewLocationEntry } from '../../../Api/ServiceRequest/serviceRequestApi';
import { getOrderRequestSchemaSubject, useOrderRequestSchemasStore } from '../../../store/OrderRequestSchema';
import {
  CheckboxInput,
  CompoundTextInput,
  CurrencyInput,
  CustomFieldTemplate,
  DateInput,
  IntegerInput,
  JSONSchemaForm,
  MultiSelectInput,
  CustomObjectFieldTemplate,
  ProductAddressInput,
  RadioNumberInput,
  SelectInput,
  ShowAdvancedButton,
  StatusSelectionInput,
  SupplierSelectionInput,
  TermSelectionInput,
  TextInput
} from '../JSONSchemaForm';

const typeOfRequestValues: string[] = [
  'New Service',
  'Move Existing Service',
  'Disconnect',
  'Replace',
  'Change',
  'Other'
];
const preferredTermOptions: string[] = ['12 Months', '24 Months', '36 Months', 'Month-to-Month', 'Other'];
const newLocationLimitText: string =
  "Add up to 4 locations that you have needs for or represent a region. We'll gather the rest of your requirements when we talk.";

export interface FormData {
  request_type: string;
  service_types: string[];
  details: string;
}

const ASSET_REQUIRED_TYPES = ['Move Existing Service', 'Change'];

// Disconnect and Other never create an Opportunity, so an attachment would have no record to land on.
const ATTACHMENT_UNSUPPORTED_TYPES = ['Disconnect', 'Other'];

// New Service and Other offer the global product catalog; every other request type
// offers only products managed in the account's inventory.
const usesAccountProducts = (requestType: string) =>
  requestType !== typeOfRequestValues[0] && requestType !== typeOfRequestValues[5];

export interface RequestSnapshot {
  request_type: string;
  preferred_term: string[];
  details: string;
  productSelection: Product | null;
  locationSelections: string[];
  newLocations: NewLocationEntry[];
  selectedAsset: InventoryAsset | null;
  selectedDisconnectInventories: InventoryAsset[];
  locationA: string | null;
  locationZ: string | null;
  schemaFormData: Record<string, unknown>;
  schemaFormErrors: RJSFValidationError[];
  attachments: Attachment[];
  opportunityId: string | null;
  status: 'pending' | 'success' | 'failed';
}

const createDefaultSnapshot = (): RequestSnapshot => ({
  request_type: typeOfRequestValues[0],
  preferred_term: [],
  details: '',
  productSelection: null,
  locationSelections: [],
  newLocations: [],
  selectedAsset: null,
  selectedDisconnectInventories: [],
  locationA: null,
  locationZ: null,
  schemaFormData: {},
  schemaFormErrors: [],
  attachments: [],
  opportunityId: null,
  status: 'pending'
});

interface SubmitOutcome {
  failedRequests: number;
  orphanedFiles: boolean;
  unsentFiles: boolean;
  refusedFiles: boolean;
}

// A request that landed without an Opportunity has no record to attach to, so resubmitting cannot help.
const hasOrphanedAttachments = (request: RequestSnapshot): boolean =>
  request.status === 'success' && !request.opportunityId && request.attachments.some(isPendingUpload);

const hasUnsentAttachments = (request: RequestSnapshot): boolean =>
  request.status === 'success' && !!request.opportunityId && request.attachments.some(isPendingUpload);

const submitOutcomeMessage = ({ failedRequests, orphanedFiles, unsentFiles, refusedFiles }: SubmitOutcome): string => {
  if (failedRequests) {
    return orphanedFiles || unsentFiles || refusedFiles
      ? `${failedRequests} request(s) failed, and some attachments need attention. Please review and resubmit.`
      : `${failedRequests} request(s) failed. Please review and resubmit.`;
  }
  if (orphanedFiles) {
    return 'Your request was sent, but its attachments could not be added. Contact your Account Manager to send them.';
  }
  if (refusedFiles && !unsentFiles) {
    return 'Your request was sent. The files marked below were not accepted, so they were not attached.';
  }
  return 'Your request was sent, but some attachments did not upload. Select Submit to try them again.';
};

const formatLocation = (item: InventoryAsset, displayCodeOnly = false) => {
  if (displayCodeOnly && item.addressInfo?.number) return item.addressInfo.number;
  return item.addressInfo?.number ? `${item.addressInfo.number} - ${item.address}` : item.address;
};

// Keep only the keys the active product's schema still renders, so shared
// fields carry over on a product switch and hidden ones are dropped.
export const pruneFormDataToSchema = (
  formData: Record<string, unknown>,
  schema: OrderRequestSchema | null
): Record<string, unknown> => {
  const allowedKeys = new Set(Object.keys(schema?.schema?.validation_schema?.properties ?? {}));
  return Object.fromEntries(Object.entries(formData).filter(([key]) => allowedKeys.has(key)));
};

// A product a permission set hides arrives with no id or name, so it offers no Type of Service.
export const managedProductsOf = (inventory: InventoryAsset[]): Product[] =>
  inventory
    .filter((i) => i.origin && i.product?.id)
    .map((i) => i.product)
    .filter((value, index, self) => self.findIndex((p) => p.id === value.id) === index)
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

export interface ServiceRequestFormProps {
  open: boolean;
  onClose: () => void;
}

export function ServiceRequestForm({ open, onClose }: ServiceRequestFormProps) {
  const [disabled, setDisabled] = useState(false);
  const [requestType, setRequestType] = useState<string>(typeOfRequestValues[0]);
  const [accountProducts, setAccountProducts] = useState<Product[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [allowedProducts, setAllowedProducts] = useState<Product[]>([]);
  const [productOptions, setProductOptions] = useState<Product[]>([]);
  const [existingInventory, setExistingInventory] = useState<InventoryAsset[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<string[]>([]);
  const [productSelection, setProductSelection] = useState<Product | null>(null);
  const [locationSelections, setLocationSelections] = useState<string[]>([]);
  const [newLocations, setNewLocations] = useState<NewLocationEntry[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<InventoryAsset | null>(null);
  const [orderRequestPrefs, setOrderRequestPrefs] = useState<OrderRequestPreferences>({
    disabledProductFamilies: [],
    disabledProducts: {},
    displayProductFamilyName: true,
    isAdvancedForm: false,
    displayLocationCode: false,
    advancedNewLocationSettings: false
  });
  // Keep a ref in sync so subscription callbacks always read the latest prefs
  const orderRequestPrefsRef = useRef(orderRequestPrefs);
  orderRequestPrefsRef.current = orderRequestPrefs;
  const [orderRequestSchemas, setOrderRequestSchemas] = useState<OrderRequestSchema[]>([]);
  const [showAdvancedLayout, setShowAdvancedLayout] = useState<boolean>(false);
  const [orderRequestSchema, setOrderRequestSchema] = useState<OrderRequestSchema | null>(null);
  const [schemaFormData, setSchemaFormData] = useState<Record<string, unknown>>({});
  const [schemaFormErrors, setSchemaFormErrors] = useState<RJSFValidationError[]>([]);
  const [selectedDisconnectInventories, setSelectedDisconnectInventories] = useState<InventoryAsset[]>([]);
  const [locationA, setLocationA] = useState<string | null>(null);
  const [locationZ, setLocationZ] = useState<string | null>(null);
  const [preferredTermSelections, setPreferredTermSelections] = useState<string[]>([]);
  const [stagedAttachments, setStagedAttachments] = useState<Attachment[]>([]);
  // Defaults to the account when exactly one is globally selected; otherwise the
  // user must pick one explicitly ("Please select an account").
  const deriveDefaultAccount = (): Account | null => {
    const scope = getAccountScope();
    return scope?.length === 1 ? scope[0] : null;
  };
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(deriveDefaultAccount);
  // Multi-request state
  const [requests, setRequests] = useState<RequestSnapshot[]>([createDefaultSnapshot()]);
  const [activeTab, setActiveTab] = useState(0);
  const [anotherRequest, setAnotherRequest] = useState(false);

  const { register, handleSubmit, reset, watch, getValues } = useForm<FormData>({
    defaultValues: {
      request_type: typeOfRequestValues[0],
      service_types: [],
      details: ''
    }
  });
  const { setSnack } = useSnackbar();
  const { fetchOrderRequestSchemas } = useOrderRequestSchemasStore();
  const { fetchInventory } = useInventory();
  const { fetchProducts } = useProductsStore();

  const productFamily = productSelection?.family || '';
  const isP2PProduct = useMemo(() => /point.to.point/i.test(productSelection?.name || ''), [productSelection]);
  const showAssetSelection = useMemo(() => {
    return ASSET_REQUIRED_TYPES.includes(requestType) && productFamily === 'Networking';
  }, [requestType, productFamily]);

  useEffect(() => {
    if (!showAssetSelection) setSelectedAsset(null);
  }, [showAssetSelection]);

  // Only multi-account customers pick an account; internal roles (Admin, Advisor, CSM,
  // SDM, Executive, Support) scope via the global header selector instead.
  const showAccountSelector =
    !userInSkipRoles((getUser()?.roles ?? []).map((role) => role.name)) && (getAccounts()?.length ?? 0) > 1;
  const accountSelectionMissing = showAccountSelector && !selectedAccount;
  // Offer the globally selected accounts; with none selected in the header, fall back to
  // all accessible accounts so the empty state can be resolved from within the form.
  const accountScope = getAccountScope();
  const selectableAccounts = accountScope?.length ? accountScope : (getAccounts() ?? []);
  const accountOptions = [...selectableAccounts].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

  const scopedInventory = useMemo(
    () =>
      showAccountSelector && selectedAccount
        ? existingInventory.filter((asset) => asset.account_id === selectedAccount.id)
        : existingInventory,
    [existingInventory, selectedAccount, showAccountSelector]
  );

  const handleAccountChange = (_: React.SyntheticEvent<Element, Event>, value: Account | null) => {
    setSelectedAccount(value);
    // Inventory-derived picks (locations, assets, account-managed products) may belong to
    // the previous account; drop them here and on any not-yet-submitted request tabs
    // (free-text new locations and global-catalog products stay).
    if (usesAccountProducts(requestType)) setProductSelection(null);
    setLocationSelections([]);
    setLocationA(null);
    setLocationZ(null);
    setSelectedAsset(null);
    setSelectedDisconnectInventories([]);
    setRequests((prev) =>
      prev.map((request) =>
        request.status === 'success'
          ? request
          : {
              ...request,
              productSelection: usesAccountProducts(request.request_type) ? null : request.productSelection,
              locationSelections: [],
              locationA: null,
              locationZ: null,
              selectedAsset: null,
              selectedDisconnectInventories: []
            }
      )
    );
  };

  useEffect(() => {
    if (open) setSelectedAccount(deriveDefaultAccount());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const widgets: RegistryWidgetsType = {
    CheckboxInput,
    SupplierSelectionInput,
    TermSelectionInput,
    StatusSelectionInput,
    ProductAddressInput,
    TextInput,
    DateInput,
    CurrencyInput,
    ShowAdvancedButton,
    IntegerInput,
    SelectInput,
    MultiSelectInput,
    RadioNumberInput,
    CompoundTextInput
  };

  const resetFormData = () => {
    setDisabled(false);
    setRequestType(typeOfRequestValues[0]);
    setProductSelection(null);
    setLocationSelections([]);
    setNewLocations([]);
    setShowAdvancedLayout(false);
    setOrderRequestSchema(null);
    setSchemaFormData({});
    setSchemaFormErrors([]);
    setSelectedAsset(null);
    setSelectedDisconnectInventories([]);
    setLocationA(null);
    setLocationZ(null);
    setPreferredTermSelections([]);
    setStagedAttachments([]);
    setSelectedAccount(deriveDefaultAccount());
    reset();
    setRequests([createDefaultSnapshot()]);
    setActiveTab(0);
    setAnotherRequest(false);
  };

  const handleClose = () => {
    resetFormData();
    onClose();
  };

  useEffect(() => {
    if (productSelection && orderRequestSchemas.length > 0 && requestType !== 'Disconnect') {
      const schema = orderRequestSchemas.find((schema) => schema.product_family === productSelection.family);
      setOrderRequestSchema(schema || null);
      setShowAdvancedLayout(isAdvancedOrderFormEnabled(orderRequestPrefs) && schema !== undefined);
      // schemaFormData is reconciled by the prune effect below, not wiped here.
    } else {
      setShowAdvancedLayout(false);
      setOrderRequestSchema(null);
    }
  }, [orderRequestPrefs, productSelection, orderRequestSchemas, requestType]);

  useEffect(() => {
    if (allProducts) {
      let filtered = allProducts;
      if (orderRequestPrefs?.disabledProductFamilies?.length > 0) {
        filtered = filtered.filter(
          (prod) => !prod.family || !orderRequestPrefs.disabledProductFamilies.includes(prod.family)
        );
      }
      if (orderRequestPrefs?.disabledProducts) {
        filtered = filtered.filter((prod) => {
          const disabledIdsForFamily = orderRequestPrefs.disabledProducts[prod.family || ''] || [];
          return !disabledIdsForFamily.includes(prod.id);
        });
      }
      setAllowedProducts(filtered);
    }
  }, [orderRequestPrefs, allProducts]);

  useEffect(() => {
    initializeProductOptions(requestType);
  }, [allowedProducts, accountProducts, requestType]);

  // The form reads from the global inventory/product stores but doesn't own the fetch, so
  // it can open (e.g. from the Orders page) before they're populated. Both store fetches
  // self-guard (inventory skips on empty scope or already-loaded; products cache), so call
  // them directly and let the stores dedupe.
  const ensureOrderFormData = () => {
    fetchInventory();
    fetchProducts();
  };

  useEffect(() => {
    const productSub = getProductSubject().subscribe((products) => {
      setAllProducts(products);
    });
    const inventorySub = getInventorySubject().subscribe((inventory) => {
      setExistingInventory(inventory);
    });

    const settingsSub = getUserPreferencesSubject().subscribe(() => {
      const prefs = getPreferencesByKey('order_request') as OrderRequestPreferences;
      if (prefs) setOrderRequestPrefs(prefs);
    });

    const schemasSub = getOrderRequestSchemaSubject().subscribe((schemas) => {
      setOrderRequestSchemas(schemas);
    });

    // A BehaviorSubject fires immediately on subscribe and again on every change, so this
    // both loads data on mount and re-loads when the account scope is set after mount
    // (e.g. a direct Orders-page load that races ahead of scope).
    const accountsSub = getSelectedAccountsSubject().subscribe(() => {
      ensureOrderFormData();
    });

    fetchOrderRequestSchemas();

    return () => {
      if (productSub) productSub.unsubscribe();
      if (inventorySub) inventorySub.unsubscribe();
      if (settingsSub) settingsSub.unsubscribe();
      if (schemasSub) schemasSub.unsubscribe();
      if (accountsSub) accountsSub.unsubscribe();
    };
  }, []);

  // Managed products drive the Type of Service options for Move/Disconnect/Replace/Change,
  // so they follow the account-scoped inventory rather than the raw store.
  useEffect(() => {
    setAccountProducts(managedProductsOf(scopedInventory));
  }, [scopedInventory]);

  const updateFilteredLocations = (inventory: InventoryAsset[] = [], filteredProduct: Product | null = null) => {
    const displayCodeOnly = orderRequestPrefsRef.current.displayLocationCode ?? false;
    const inv = inventory.length > 0 ? inventory : scopedInventory;
    let locations: string[] = [];

    if (['New Service', 'Disconnect'].includes(requestType)) {
      locations = inv
        .filter((item) => !!item.address)
        .map((item) => formatLocation(item, displayCodeOnly))
        .filter((value, index, self) => self.findIndex((p) => p === value) === index)
        .sort();
      setFilteredLocations(locations);
    } else {
      locations = inv
        .filter((item) => !!item.address && (!filteredProduct || filteredProduct.id === item.product?.id))
        .map((item) => formatLocation(item, displayCodeOnly))
        .filter((value, index, self) => self.findIndex((p) => p === value) === index)
        .sort();
      setFilteredLocations(locations);
    }
    setLocationSelections(locationSelections.filter((loc) => locations.findIndex((option) => option === loc) > -1));
  };

  // Single source of truth for the Locations dropdown: recompute whenever any input changes —
  // inventory (the shared store may already be warm before this form mounts, e.g. opening the
  // form on Home after Orders loaded it), the request type or selected product (only "Move
  // Existing Service" filters locations by product), or the location-code preference.
  useEffect(() => {
    const filterProduct = requestType === 'Move Existing Service' ? productSelection : null;
    updateFilteredLocations(scopedInventory, filterProduct);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedInventory, requestType, productSelection, orderRequestPrefs.displayLocationCode]);

  const attachmentsSupported = !ATTACHMENT_UNSUPPORTED_TYPES.includes(requestType);

  // Keeping them would strand files in a request that can never carry them, behind a hidden control.
  useEffect(() => {
    if (!attachmentsSupported) setStagedAttachments([]);
  }, [attachmentsSupported]);

  // The queue below sees only the active tab, and this form keeps files on every tab until Submit.
  const hasUnsentFiles =
    stagedAttachments.some(isUnsentFile) ||
    requests.some((request, index) => index !== activeTab && request.attachments.some(isUnsentFile));
  useBeforeUnload(
    useCallback(
      (event) => {
        if (!hasUnsentFiles) return;
        event.preventDefault();
        event.returnValue = '';
      },
      [hasUnsentFiles]
    )
  );

  const addStagedAttachments = (files: File[]) =>
    setStagedAttachments((current) => [...current, ...stageAttachments(files)]);

  const removeStagedAttachment = (attachmentId: string) =>
    setStagedAttachments((current) => current.filter((attachment) => attachment.id !== attachmentId));

  // Dropping the row would hide a transfer that keeps running and still lands the file.
  const cancelStagedUpload = (attachmentId: string) =>
    stagedAttachments.find((attachment) => attachment.id === attachmentId)?.cancelSource.cancel();

  const requeueStagedAttachment = (attachmentId: string) =>
    setStagedAttachments((current) =>
      current.map((attachment) =>
        attachment.id === attachmentId ? { ...attachment, status: 'queued', progress: 0 } : attachment
      )
    );

  const captureSnapshot = (): RequestSnapshot => ({
    request_type: requestType,
    preferred_term: preferredTermSelections,
    details: getValues('details'),
    productSelection,
    locationSelections,
    newLocations,
    selectedAsset,
    selectedDisconnectInventories,
    locationA,
    locationZ,
    schemaFormData,
    schemaFormErrors,
    attachments: stagedAttachments,
    opportunityId: requests[activeTab]?.opportunityId ?? null,
    status: requests[activeTab]?.status ?? 'pending'
  });

  const restoreSnapshot = (snapshot: RequestSnapshot) => {
    setRequestType(snapshot.request_type);
    setProductSelection(snapshot.productSelection);
    setLocationSelections(snapshot.locationSelections);
    setNewLocations(snapshot.newLocations);
    setSelectedAsset(snapshot.selectedAsset);
    setSelectedDisconnectInventories(snapshot.selectedDisconnectInventories);
    setLocationA(snapshot.locationA);
    setLocationZ(snapshot.locationZ);
    setPreferredTermSelections(snapshot.preferred_term);
    setSchemaFormData(snapshot.schemaFormData);
    setSchemaFormErrors(snapshot.schemaFormErrors);
    setStagedAttachments(snapshot.attachments);
    reset({
      request_type: snapshot.request_type,
      details: snapshot.details,
      service_types: []
    });
  };

  const buildServiceRequestFromSnapshot = (snap: RequestSnapshot): ServiceRequest => {
    const isP2P = /point.to.point/i.test(snap.productSelection?.name || '');
    const snapFamily = snap.productSelection?.family || '';
    const snapShowAssetSelection = ASSET_REQUIRED_TYPES.includes(snap.request_type) && snapFamily === 'Networking';

    const resolveDisplayedLocation = (locationValue: string): string => {
      if (!orderRequestPrefsRef.current.displayLocationCode) return locationValue;
      const match = scopedInventory.find((item) => item.addressInfo?.number === locationValue);
      return match ? formatLocation(match, false) : locationValue;
    };

    const requestBody: ServiceRequest = {
      request_type: snap.request_type,
      service_type: snap.productSelection ? `${snap.productSelection.family} - ${snap.productSelection.name}` : '',
      locations: snap.locationSelections.map(resolveDisplayedLocation),
      new_locations: snap.newLocations,
      asset_id: snapShowAssetSelection ? snap.selectedAsset?.id : undefined,
      details: snap.details
    };

    if (snap.request_type === 'Disconnect') {
      requestBody.asset_id = snap.selectedDisconnectInventories[0]?.id;
      requestBody.locations = snap.selectedDisconnectInventories.map((i) => i.address);
      requestBody.new_locations = [];
      requestBody.preferred_term = undefined;
    }

    // For New Service and Replace, collect asset IDs for all existing inventory selections
    // so the backend can use their SF locationIds directly instead of address-based lookup
    if (['New Service', 'Replace'].includes(snap.request_type) && snap.locationSelections.length > 0) {
      const matchedAssets = snap.locationSelections
        .map((loc) =>
          scopedInventory.find((item) => formatLocation(item, orderRequestPrefsRef.current.displayLocationCode) === loc)
        )
        .filter((item): item is InventoryAsset => item !== undefined);
      if (matchedAssets.length > 0) {
        if (snap.request_type === 'Replace') {
          requestBody.asset_id = matchedAssets[0].id; // first asset drives Source_Id_c on quote/opportunity
        }
        requestBody.asset_ids = matchedAssets.map((item) => item.id);
      }
    }

    if (isP2P && snap.request_type !== 'Change' && snap.request_type !== 'Disconnect') {
      requestBody.locations = [snap.locationA, snap.locationZ]
        .filter((v): v is string => Boolean(v))
        .map(resolveDisplayedLocation);
      requestBody.new_locations = snap.newLocations;
    }

    if (Object.keys(snap.schemaFormData).length > 0) {
      requestBody.advanced_fields_type = snap.productSelection?.family;
      requestBody.advanced_fields = snap.schemaFormData;
    }

    if (snap.request_type === typeOfRequestValues[0]) {
      requestBody.preferred_term = snap.preferred_term.length > 0 ? snap.preferred_term.join(', ') : undefined;
    }

    return requestBody;
  };

  // The Opportunity a file hangs off is created by the submission, so uploads can only run after it.
  const uploadAttachmentsFor = (snapshot: RequestSnapshot, tabIndex: number): Promise<Attachment[]> =>
    uploadStagedAttachments(snapshot.attachments, snapshot.opportunityId ?? '', (updated) => {
      if (tabIndex !== activeTab) return;
      setStagedAttachments((current) =>
        current.map((attachment) => (attachment.id === updated.id ? updated : attachment))
      );
    });

  const revealTab = (snapshots: RequestSnapshot[], index: number) => {
    if (index < 0 || index === activeTab) return;
    setActiveTab(index);
    restoreSnapshot(snapshots[index]);
  };

  const submitRequest = async (snapshot: RequestSnapshot, tabIndex: number): Promise<RequestSnapshot> => {
    let request = snapshot;

    if (request.status !== 'success') {
      const requestBody = buildServiceRequestFromSnapshot(request);
      const result = await addServiceRequest(requestBody, showAccountSelector ? selectedAccount?.id : undefined);

      if (!result) return { ...request, status: 'failed' };

      request = { ...request, status: 'success', opportunityId: result.opportunity_id ?? null };
    }

    if (!request.attachments.some(isPendingUpload)) return request;

    return { ...request, attachments: await uploadAttachmentsFor(request, tabIndex) };
  };

  const handleSubmitAll = async (): Promise<void> => {
    if (accountSelectionMissing) return;
    setDisabled(true);

    const resultRequests = [...requests];
    resultRequests[activeTab] = captureSnapshot();

    for (let i = 0; i < resultRequests.length; i++) {
      resultRequests[i] = await submitRequest(resultRequests[i], i);
      setRequests([...resultRequests]);
    }

    const outcome: SubmitOutcome = {
      failedRequests: resultRequests.filter((request) => request.status === 'failed').length,
      orphanedFiles: resultRequests.some(hasOrphanedAttachments),
      unsentFiles: resultRequests.some(hasUnsentAttachments),
      refusedFiles: resultRequests.some((request) => request.attachments.some(refusedFile))
    };

    if (!outcome.failedRequests && !outcome.orphanedFiles && !outcome.unsentFiles && !outcome.refusedFiles) {
      setSnack({ message: 'Your email has been sent successfully.', type: 'success', open: true });
      resetFormData();
      onClose();
      return;
    }

    setDisabled(false);
    setSnack({ message: submitOutcomeMessage(outcome), type: 'error', open: true });
    revealTab(
      resultRequests,
      outcome.failedRequests
        ? resultRequests.findIndex((request) => request.status === 'failed')
        : resultRequests.findIndex((request) => request.attachments.some((a) => isPendingUpload(a) || refusedFile(a)))
    );
  };

  const handleContinue = () => {
    if (requests.length >= 5) return;
    const snapshot = captureSnapshot();
    const updatedRequests = [...requests];
    updatedRequests[activeTab] = snapshot;
    const newSnapshot: RequestSnapshot = {
      ...snapshot,
      schemaFormErrors: [],
      attachments: [],
      opportunityId: null,
      status: 'pending'
    };
    updatedRequests.push(newSnapshot);
    const newTab = updatedRequests.length - 1;
    setRequests(updatedRequests);
    setActiveTab(newTab);
    setAnotherRequest(false);
    restoreSnapshot(newSnapshot);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    if (newValue === activeTab) return;
    const snapshot = captureSnapshot();
    const updatedRequests = [...requests];
    updatedRequests[activeTab] = snapshot;
    setRequests(updatedRequests);
    setActiveTab(newValue);
    restoreSnapshot(updatedRequests[newValue]);
    setAnotherRequest(false);
  };

  const handleRemoveTab = (index: number) => {
    if (requests.length <= 1 || requests[index].status === 'success') return;
    const withCurrent = [...requests];
    withCurrent[activeTab] = captureSnapshot();
    const updatedRequests = withCurrent.filter((_, i) => i !== index);
    const newActiveTab = activeTab >= index && activeTab > 0 ? activeTab - 1 : activeTab;
    setRequests(updatedRequests);
    setActiveTab(newActiveTab);
    restoreSnapshot(updatedRequests[newActiveTab]);
    setAnotherRequest(false);
  };

  const productSelectionChanged = (e: React.SyntheticEvent<Element, Event>, value: Product | null) => {
    setProductSelection(value);
    if (requestType === 'Disconnect') {
      setSelectedDisconnectInventories([]);
    }
    if (requestType === typeOfRequestValues[1]) {
      // locations recompute via the effect above (productSelection is in its deps)
      setSelectedAsset(null);
    }
  };

  const newLocationsChanged = (newValue: NewLocationEntry[]) => {
    setNewLocations(newValue);
  };

  const requestTypeChanged = (event: SelectChangeEvent) => {
    setRequestType(event.target.value);
    setProductSelection(null);
    initializeProductOptions(event.target.value);
  };

  const requestSchema = useMemo(() => {
    if (!orderRequestSchema) return null;

    const clonedSchema = JSON.parse(JSON.stringify(orderRequestSchema));
    if (requestType === 'New Service') {
      if (clonedSchema.schema.validation_schema.properties) {
        delete clonedSchema.schema.validation_schema.properties.existing_circuit_details;
      }
      if (clonedSchema.schema.ui_schema) {
        delete clonedSchema.schema.ui_schema.existing_circuit_details;
        if (clonedSchema.schema.ui_schema['ui:options']?.order) {
          clonedSchema.schema.ui_schema['ui:options'].order = clonedSchema.schema.ui_schema['ui:options'].order.filter(
            (field: string) => field !== 'existing_circuit_details'
          );
        }
      }
    }
    if (requestType === 'Change') {
      const fieldsToRemove = [
        'desired_install_date',
        'ips',
        'ip_requirements',
        'cer_per',
        'speed',
        'location_a',
        'location_z',
        'diversifying_from',
        'handoff_requirement',
        'existing_circuit_details',
        'billing_same_as_install',
        'billing_address'
      ];
      const valSchema = clonedSchema.schema.validation_schema;
      const uiSchema = clonedSchema.schema.ui_schema;
      fieldsToRemove.forEach((field) => {
        if (valSchema.properties) delete valSchema.properties[field];
        if (uiSchema) delete uiSchema[field];
      });
      if (valSchema.required) {
        valSchema.required = valSchema.required.filter((f: string) => !fieldsToRemove.includes(f));
      }
      // Relabelled rather than renamed: the portal reads this request back under the schema's own key.
      if (valSchema.properties && valSchema.properties.location_delivery_details) {
        if (uiSchema && uiSchema.location_delivery_details) {
          uiSchema.location_delivery_details = {
            ...uiSchema.location_delivery_details,
            'ui:title': 'Change Request Details',
            'ui:placeholder': 'ie: increase bandwidth?'
          };
        }
        if (uiSchema['ui:options']?.order) {
          uiSchema['ui:options'].order = uiSchema['ui:options'].order.filter(
            (f: string) => !fieldsToRemove.includes(f)
          );
        }
      }
    }

    // Product-type-based field visibility
    if (productSelection) {
      const productName = productSelection.name || '';
      const isP2P = /point.to.point/i.test(productName);
      const isDIA = /dedicated internet access/i.test(productName) || productName === 'DIA';
      const isBroadband = /broadband/i.test(productName);
      const isMPLS = /multi.?protocol.?label.?switching|mpls/i.test(productName);
      const isSpeedApplicable = isDIA || isBroadband || isP2P || isMPLS;

      const valSchema = clonedSchema.schema.validation_schema;
      const uiSchema = clonedSchema.schema.ui_schema;

      const removeFields = (fields: string[]) => {
        fields.forEach((field) => {
          if (valSchema.properties) delete valSchema.properties[field];
          if (uiSchema) delete uiSchema[field];
          if (uiSchema['ui:options']?.order) {
            uiSchema['ui:options'].order = uiSchema['ui:options'].order.filter((f: string) => f !== field);
          }
          if (valSchema.required) {
            valSchema.required = valSchema.required.filter((f: string) => f !== field);
          }
        });
      };

      if (!isSpeedApplicable) removeFields(['speed']);

      if (isDIA) {
        // DIA: keep ip_requirements (text input), remove ips (Static/DHCP radio)
        removeFields(['ips', 'cer_per']);
      } else if (isMPLS) {
        // MPLS: IP not needed; cer_per checkbox comes from schema
        removeFields(['ips', 'ip_requirements']);
      } else if (isP2P) {
        // P2P: IP not needed; remove both ip fields
        removeFields(['ips', 'ip_requirements', 'cer_per']);
      } else {
        // Broadband and others: keep ips (Static/DHCP), remove ip_requirements
        removeFields(['ip_requirements', 'cer_per']);
      }
    }

    return clonedSchema;
  }, [orderRequestSchema, requestType, productSelection]);

  const dynamicUiSchema = useMemo(() => {
    if (!requestSchema) return undefined;
    const uiSchema = requestSchema.schema.ui_schema;
    if (!uiSchema) return uiSchema;
    if (schemaFormData.billing_same_as_install) {
      return {
        ...uiSchema,
        billing_address: { ...(uiSchema.billing_address || {}), 'ui:disabled': true }
      };
    }
    return uiSchema;
  }, [requestSchema, schemaFormData.billing_same_as_install]);

  // Reconcile schemaFormData with the active product's schema: keep shared
  // fields, drop ones it no longer renders, and clear it when no schema is
  // active so stale advanced fields are never submitted.
  useEffect(() => {
    setSchemaFormData((prev) => {
      const pruned = pruneFormDataToSchema(prev, requestSchema);
      return Object.keys(pruned).length === Object.keys(prev).length ? prev : pruned;
    });
  }, [requestSchema]);

  const initializeProductOptions = (requestType: string) => {
    setProductOptions(usesAccountProducts(requestType) ? accountProducts : allowedProducts);
  };

  const locationSelectionChanged = (e: React.SyntheticEvent<Element, Event>, value: string[]) => {
    setLocationSelections(value);
  };

  const handleSchemaFormChange = (formData: Record<string, unknown> | null) => {
    setSchemaFormData(formData || {});
  };

  const handleSchemaFormError = (errors: RJSFValidationError[]) => {
    setSchemaFormErrors(errors);
  };

  const isFormValid = () => {
    if (accountSelectionMissing) return false;
    const hasRequestType = !!requestType;
    const hasProductSelection = !!productSelection;
    if (requestType === 'Disconnect') {
      const hasInventory = selectedDisconnectInventories.length > 0;
      return hasRequestType && hasProductSelection && hasInventory && !!watch('details');
    }

    const hasLocationSelection =
      requestType === 'Change' ||
      (isP2PProduct
        ? locationA !== null || locationZ !== null || newLocations.length > 0
        : locationSelections.length > 0 ||
          (newLocations.length > 0 && newLocations.every((loc) => loc.address.length > 0)));

    if (showAssetSelection && !selectedAsset) return false;

    if (showAdvancedLayout) {
      const isSchemaFormValid = schemaFormErrors.length === 0;

      // Additional check: ensure required schema fields have values
      // This catches cases where RJSF validation hasn't run yet
      const requiredFields = requestSchema?.schema?.validation_schema?.required || [];
      const hasRequiredFieldValues = requiredFields.every((fieldName: string) => {
        const value = schemaFormData[fieldName];
        // Check that required fields have some meaningful value
        return value !== null && value !== undefined && value !== '';
      });

      return (
        hasRequestType && hasProductSelection && hasLocationSelection && isSchemaFormValid && hasRequiredFieldValues
      );
    }

    return hasRequestType && hasProductSelection;
  };

  const filteredInventory = useMemo(() => {
    return scopedInventory.filter((asset) => {
      if (asset.product?.family !== 'Networking' || !asset.address) return false;
      if (requestType === 'Move Existing Service' && productSelection) {
        return asset.product?.id === productSelection.id;
      }
      return true;
    });
  }, [scopedInventory, productSelection, requestType]);

  const isShowWide = orderRequestSchema && showAdvancedLayout;
  const isCurrentTabSucceeded = requests[activeTab]?.status === 'success';
  return (
    <Dialog
      maxWidth={isShowWide ? 'md' : 'xs'}
      fullWidth
      open={open}
      onClose={disabled ? undefined : handleClose}>
      <div style={{ padding: '40px' }}>
        <TextBlock className="text-xl">Start a New Request</TextBlock>
        <TextSpan
          className="py-2 text-[13px] block mb-2"
          color="grey7"
          size="sm14">
          Submit a new request and your Account Manager will reach out to you shortly to learn more.
        </TextSpan>
        {requests.length > 1 && (
          <Tabs
            value={activeTab}
            onChange={disabled ? undefined : handleTabChange}
            sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
            {requests.map((req, index) => (
              <Tab
                key={index}
                value={index}
                label={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    Request {index + 1}
                    {req.status === 'success' && <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />}
                    {req.status === 'failed' && <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />}
                    {req.status === 'pending' && requests.length > 1 && (
                      <IconButton
                        size="small"
                        disabled={disabled}
                        aria-label={`Remove request ${index + 1}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRemoveTab(index);
                        }}
                        sx={{ ml: 0.5, p: 0, fontSize: '1rem', lineHeight: 1 }}>
                        ×
                      </IconButton>
                    )}
                  </span>
                }
              />
            ))}
          </Tabs>
        )}
        <form
          data-cy="service-request"
          aria-label="Service Request"
          onSubmit={handleSubmit(handleSubmitAll)}
          className={`flex flex-col items-start gap-6 ${isShowWide ? 'w-full max-w-6xl' : 'w-full'}`}>
          {/* Hidden submit button so pressing Enter in any field submits the form */}
          <button
            type="submit"
            tabIndex={-1}
            aria-hidden="true"
            style={{ display: 'none' }}
          />
          {showAccountSelector && (
            <div className="w-full pt-5">
              <div
                className="text-gray-700"
                style={{ marginBottom: '0.5rem' }}>
                Account
              </div>
              <Autocomplete
                disabled={disabled}
                sx={{ width: '100%' }}
                size="small"
                options={accountOptions}
                value={selectedAccount}
                onChange={handleAccountChange}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option: Account) => option?.name ?? ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Account"
                    data-cy="account-selector"
                    error={accountSelectionMissing}
                    helperText={accountSelectionMissing ? 'Please select an account' : undefined}
                    inputProps={{
                      ...params.inputProps,
                      'aria-label': 'account',
                      required: accountSelectionMissing
                    }}
                  />
                )}
              />
            </div>
          )}
          <div
            className={`w-full ${showAccountSelector ? '' : 'pt-5'} ${isShowWide ? 'columns-1 md:columns-2 gap-6' : ''}`}>
            {(showAdvancedLayout || showAccountSelector) && (
              <div
                className="text-gray-700"
                style={{ marginBottom: '0.5rem' }}>
                Request Details
              </div>
            )}
            <div style={{ breakInside: 'avoid', marginBottom: '1.5rem' }}>
              <TextField
                select
                required
                disabled={disabled}
                sx={{ width: '100%' }}
                label="Type of Request"
                id="request_type_select"
                defaultValue={typeOfRequestValues[0]}
                data-cy="request_type"
                size="small"
                aria-label="type of request"
                {...register('request_type', { onChange: requestTypeChanged })}>
                {typeOfRequestValues.map((value) => (
                  <MenuItem
                    key={value}
                    value={value}>
                    {value}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            <div style={{ breakInside: 'avoid', marginBottom: '1.5rem' }}>
              <Autocomplete
                disabled={disabled}
                sx={{ width: '100%' }}
                size="small"
                options={productOptions}
                value={productSelection}
                onChange={productSelectionChanged}
                getOptionLabel={(option: Product) =>
                  orderRequestPrefs.displayProductFamilyName ? `${option.family} - ${option.name}` : option.name
                }
                renderOption={(props, option) => (
                  <li
                    {...props}
                    key={option.id}>
                    {orderRequestPrefs.displayProductFamilyName ? `${option.family} - ${option.name}` : option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Type of Service*"
                    inputProps={{
                      ...params.inputProps,
                      required: productSelection === null
                    }}
                  />
                )}
              />
            </div>
            {requestType === 'Disconnect' && (
              <>
                <div style={{ breakInside: 'avoid', marginBottom: '1.5rem' }}>
                  <Autocomplete
                    multiple
                    disabled={disabled}
                    sx={{ width: '100%' }}
                    size="small"
                    options={filteredInventory}
                    value={selectedDisconnectInventories}
                    onChange={(_, newValue) => setSelectedDisconnectInventories(newValue)}
                    getOptionLabel={(option: InventoryAsset) => {
                      if (option === null) return '';
                      if (orderRequestPrefs.displayLocationCode && option.addressInfo?.number)
                        return option.addressInfo.number;
                      if (option.addressInfo?.number) return `${option.addressInfo.number} - ${option.address}`;
                      return option.address;
                    }}
                    filterSelectedOptions
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          orderRequestPrefs.displayLocationCode
                            ? 'Existing Inventory Location Code'
                            : 'Existing Inventory'
                        }
                        inputProps={{
                          ...params.inputProps,
                          required: selectedDisconnectInventories.length === 0
                        }}
                      />
                    )}
                  />
                </div>
                <div style={{ breakInside: 'avoid', marginBottom: '1.5rem' }}>
                  <TextField
                    label="Disconnect Request Details"
                    placeholder="ie: location closing"
                    id="details_textarea"
                    data-cy="details"
                    aria-label="details"
                    sx={{ width: '100%' }}
                    disabled={disabled}
                    minRows={3}
                    multiline
                    {...register('details')}></TextField>
                </div>
              </>
            )}
            {showAssetSelection && requestType !== 'Disconnect' && (
              <div style={{ breakInside: 'avoid', marginBottom: '1.5rem' }}>
                <Autocomplete
                  id="asset-selection"
                  sx={{ width: '100%' }}
                  size="small"
                  options={filteredInventory}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option: InventoryAsset) => {
                    if (option === null) return '';
                    const address =
                      orderRequestPrefs.displayLocationCode && option.addressInfo?.number
                        ? option.addressInfo.number
                        : option.addressInfo?.number
                          ? `${option.addressInfo.number} - ${option.address}`
                          : option.address;
                    if (requestType === 'Move Existing Service' && option.supplier?.name) {
                      const detail = option.bandwidth
                        ? ` — ${option.bandwidth}`
                        : option.circuitIDs
                          ? ` (${option.circuitIDs})`
                          : '';
                      return `${option.supplier.name}, ${address}${detail}`;
                    }
                    return address;
                  }}
                  value={selectedAsset}
                  onChange={(_, newValue) => {
                    setSelectedAsset(newValue);
                  }}
                  disabled={disabled}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        orderRequestPrefs.displayLocationCode
                          ? 'Existing Inventory Location Code'
                          : 'Existing Inventory'
                      }
                      inputProps={{
                        ...params.inputProps,
                        required: selectedAsset === null
                      }}
                    />
                  )}
                />
              </div>
            )}
            {requestType !== 'Change' && requestType !== 'Disconnect' && (
              <>
                {isP2PProduct ? (
                  <>
                    <div style={{ breakInside: 'avoid', marginBottom: '1.5rem' }}>
                      <Autocomplete
                        disabled={disabled}
                        sx={{ width: '100%' }}
                        size="small"
                        options={filteredLocations}
                        value={locationA}
                        onChange={(_, newValue) => setLocationA(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={orderRequestPrefs.displayLocationCode ? 'Location Code - Location A' : 'Location A'}
                            inputProps={{ ...params.inputProps }}
                          />
                        )}
                      />
                    </div>
                    <div style={{ breakInside: 'avoid', marginBottom: '1.5rem' }}>
                      <Autocomplete
                        disabled={disabled}
                        sx={{ width: '100%' }}
                        size="small"
                        options={filteredLocations}
                        value={locationZ}
                        onChange={(_, newValue) => setLocationZ(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={orderRequestPrefs.displayLocationCode ? 'Location Code - Location Z' : 'Location Z'}
                            inputProps={{ ...params.inputProps }}
                          />
                        )}
                      />
                    </div>
                    <div style={{ breakInside: 'avoid', marginBottom: '1.5rem' }}>
                      <MultiNewLocationInput
                        key={`p2p-new-locations-${activeTab}`}
                        linkClassName="text-[indigo] text-sm"
                        limit={2}
                        limitText={newLocationLimitText}
                        onLocationsChange={newLocationsChanged}
                        showAdvancedSettings={orderRequestPrefs.advancedNewLocationSettings}
                        initialLocations={newLocations}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ breakInside: 'avoid', marginBottom: '1.5rem' }}>
                      <Autocomplete
                        multiple
                        disabled={disabled}
                        sx={{ width: '100%' }}
                        size="small"
                        options={filteredLocations}
                        value={locationSelections}
                        onChange={locationSelectionChanged}
                        getOptionDisabled={(option) =>
                          locationSelections.length > 3 && !locationSelections.includes(option)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            helperText={
                              locationSelections.length > 3 &&
                              "Select up to 4 locations that you have needs for or represent a region. We'll gather the rest of your requirements when we talk."
                            }
                            label={
                              orderRequestPrefs.displayLocationCode
                                ? requestType === 'Move Existing Service'
                                  ? 'To: Location Code'
                                  : 'Location Code'
                                : requestType === 'Move Existing Service'
                                  ? 'To: Location'
                                  : 'Locations'
                            }
                            inputProps={{
                              ...params.inputProps
                            }}
                          />
                        )}
                      />
                    </div>
                    <div style={{ breakInside: 'avoid', marginBottom: '1.5rem' }}>
                      <MultiNewLocationInput
                        key={`new-locations-${activeTab}`}
                        linkClassName="text-[indigo] text-sm"
                        limit={4}
                        limitText={newLocationLimitText}
                        onLocationsChange={newLocationsChanged}
                        showAdvancedSettings={orderRequestPrefs.advancedNewLocationSettings}
                        initialLocations={newLocations}
                      />
                    </div>
                  </>
                )}
                <div style={{ breakInside: 'avoid', marginBottom: '1.5rem' }}>
                  <TextField
                    select
                    sx={{ width: '100%' }}
                    label="Preferred Term"
                    id="preferred_term_select"
                    value={preferredTermSelections}
                    data-cy="preferred_term"
                    size="small"
                    aria-label="preferred term"
                    disabled={disabled || requestType !== typeOfRequestValues[0]}
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) => (selected as string[]).join(', ')
                    }}
                    onChange={(e) => setPreferredTermSelections(e.target.value as unknown as string[])}>
                    {preferredTermOptions.map((value) => (
                      <MenuItem
                        key={value}
                        value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
              </>
            )}
            {!showAdvancedLayout && requestType !== 'Disconnect' && (
              <div style={{ breakInside: 'avoid', marginBottom: '1.5rem' }}>
                <TextField
                  label="Details for UPSTACK to Know"
                  id="details_textarea"
                  data-cy="details"
                  aria-label="details"
                  sx={{ width: '100%' }}
                  disabled={disabled}
                  minRows={3}
                  multiline
                  {...register('details')}></TextField>
              </div>
            )}
            {requestSchema && showAdvancedLayout && (
              <JSONSchemaForm
                formKey={`order-request-form-${requestSchema.id}-${productSelection?.id ?? ''}-${activeTab}`}
                schema={requestSchema.schema.validation_schema}
                uiSchema={dynamicUiSchema || requestSchema.schema.ui_schema}
                formData={schemaFormData}
                onChangeHandler={handleSchemaFormChange}
                onErrorHandler={handleSchemaFormError}
                rjsfProps={{
                  widgets: widgets,
                  templates: { FieldTemplate: CustomFieldTemplate, ObjectFieldTemplate: CustomObjectFieldTemplate },
                  tagName: 'div',
                  formContext: { gap: requestType === 'Change' ? '4rem' : '1.5rem' }
                }}
              />
            )}
          </div>
          {attachmentsSupported && (
            <div
              className="flex w-full flex-col gap-2"
              data-cy="order-request-attachments">
              {stagedAttachments.length > 0 && (
                <AttachmentQueue
                  queuedAttachments={stagedAttachments}
                  cancelUpload={cancelStagedUpload}
                  retryUpload={requeueStagedAttachment}
                  removeAttachment={disabled ? undefined : removeStagedAttachment}
                  showProgressPercentage
                />
              )}
              {!disabled && (
                <AttachmentTableCTA
                  addAttachments={addStagedAttachments}
                  label="Add Attachments"
                />
              )}
            </div>
          )}
          <hr className="w-full" />
          <div className="flex w-full flex-row items-center gap-3">
            {!isCurrentTabSucceeded && requests.length < 5 && (
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none flex-1">
                <input
                  type="checkbox"
                  checked={anotherRequest}
                  onChange={(e) => setAnotherRequest(e.target.checked)}
                  disabled={disabled || !isFormValid()}
                  className="h-4 w-4 cursor-pointer accent-indigo-600"
                  data-cy="another-request-checkbox"
                />
                Do you have another request at this Location?
              </label>
            )}
            <div className="flex gap-3 ml-auto">
              <button
                className={DEFAULT_CANCEL_BUTTON_STYLE}
                onClick={handleClose}
                disabled={disabled}
                type="button">
                Cancel
              </button>
              {anotherRequest ? (
                <button
                  type="button"
                  disabled={disabled || !isFormValid()}
                  className={DEFAULT_ADVANCE_BUTTON_STYLE}
                  data-cy="continue-button"
                  onClick={handleContinue}>
                  Continue
                </button>
              ) : (
                <button
                  type="button"
                  disabled={disabled || !isFormValid()}
                  className={DEFAULT_ADVANCE_BUTTON_STYLE}
                  onClick={handleSubmitAll}>
                  {disabled ? (
                    <CircularProgress
                      size={13}
                      color="inherit"
                    />
                  ) : (
                    'Submit'
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </Dialog>
  );
}
